import { ListingThumbnail } from 'app/components/listing-thumbnail';
import { Column, Row } from 'app/design/primitives/box';
import { Button } from 'app/design/primitives/button';
import { MultiImagePicker } from 'app/design/primitives/multi-image-picker';
import { Accessory } from 'app/design/primitives/page/accessory';
import { Text } from 'app/design/primitives/text';
import type { ListingSubtype, ListingType } from 'app/types/listing';
import type { ImagePickerAsset } from 'expo-image-picker';
import { IconPhoto, IconWorld, IconX } from '@tabler/icons-react-native';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Pressable, StyleSheet, TextInput } from 'react-native';
import { createTheme as createDSTheme } from 'app/shopify';
import { ICON_STROKE_WIDTH } from 'app/utils/constants';

const theme = createDSTheme();

interface PostComposerProps {
  loan: PostLoanContext;
  text: string;
  pictures: ImagePickerAsset[];
  onTextChange: (text: string) => void;
  onPicturesChange: (pictures: ImagePickerAsset[]) => void;
  showPublicNotice: boolean;
  onDismissPublicNotice: () => void;
}

interface PostLoanContext {
  listing: {
    item: { name: string };
    picture?: string | null;
    subtype?: ListingSubtype;
    type: ListingType;
  };
}

export function PostComposer({
  loan,
  text,
  pictures,
  onTextChange,
  onPicturesChange,
  showPublicNotice,
  onDismissPublicNotice,
}: PostComposerProps) {
  const { t } = useTranslation('posts');
  const { t: tImagePicker } = useTranslation('image-picker');
  const isNearCharacterLimit = text.length >= 1800;

  return (
    <Column flex={1} gap="spacing.general.x5" style={styles.pageContent}>
      <Text color="text.secondary" lineHeight={22} variant="bodyBase">
        {t('newPost.intro')}
      </Text>

      {showPublicNotice ? (
        <Row alignItems="center" gap="spacing.general.x3" style={styles.noticeChip}>
          <Column alignItems="center" justifyContent="center" style={styles.noticeIcon}>
            <IconWorld color={theme.colors['icon.primary']} size={20} strokeWidth={ICON_STROKE_WIDTH} />
          </Column>
          <Column flex={1} gap="spacing.general.x0,5">
            <Text style={styles.noticeEyebrow}>{t('newPost.publicBadge')}</Text>
            <Text fontSize={12} style={styles.noticeText}>
              {t('newPost.publicHint')}
            </Text>
          </Column>
          <Pressable
            accessibilityLabel={t('newPost.dismissPublicHint')}
            accessibilityRole="button"
            hitSlop={10}
            onPress={onDismissPublicNotice}
            style={styles.noticeDismiss}
          >
            <IconX strokeWidth={ICON_STROKE_WIDTH} color={theme.colors['icon.secondary']} />
          </Pressable>
        </Row>
      ) : null}

      <TransactionSummary loan={loan} />

      <Column gap="spacing.general.x3" style={styles.section}>
        <Column gap="spacing.general.x1">
          <Text fontWeight="700" variant="bodyLarge">
            {t('newPost.storyTitle')}
          </Text>
          <Text fontSize={13} style={styles.captionHint}>
            {t('newPost.storyHint')}
          </Text>
        </Column>
        <Column flex={1} gap="spacing.general.x2" style={styles.captionField}>
          <TextInput
            maxLength={2000}
            multiline
            onChangeText={onTextChange}
            placeholder={t('newPost.textPlaceholder')}
            placeholderTextColor={theme.colors['text.placeholder']}
            style={styles.captionInput}
            textAlignVertical="top"
            value={text}
          />
          {isNearCharacterLimit ? (
            <Text fontSize={11} style={styles.captionCount}>
              {text.length}/2000
            </Text>
          ) : null}
        </Column>
      </Column>

      <Column gap="spacing.general.x3" style={styles.section}>
        <Row alignItems="center" gap="spacing.general.x2">
          <IconPhoto color={theme.colors['icon.primary']} size={20} strokeWidth={ICON_STROKE_WIDTH} />
          <Text fontWeight="700" variant="bodyLarge">
            {t('newPost.addPhotos')}
          </Text>
          <Text fontSize={12} style={styles.optionalLabel}>
            {t('newPost.optional')}
          </Text>
        </Row>
        <MultiImagePicker
          emptyHint={t('newPost.photoHint')}
          images={pictures}
          onChange={onPicturesChange}
          thumbnailSize={88}
          title={tImagePicker('add')}
        />
      </Column>
    </Column>
  );
}

export function PostComposerAction({
  disabled,
  loading,
  onPress,
  label,
}: {
  disabled: boolean;
  loading: boolean;
  onPress: () => void;
  label: string;
}) {
  return (
    <Accessory>
      <Column style={styles.actionBar}>
        <Button
          disabled={disabled}
          justifyContent="center"
          loading={loading}
          minHeight={56}
          onPress={onPress}
          style={{
            backgroundColor: disabled ? theme.colors['surface.tertiary-neutral'] : theme.colors['surface.brand.strong'],
            borderRadius: theme.borderRadii['radius.xl'],
          }}
          variant="primary"
        >
          <Text
            color={disabled ? 'text.secondary' : 'surface.tertiary-neutral'}
            fontWeight="700"
            textAlign="center"
            variant="bodyBase"
          >
            {label}
          </Text>
        </Button>
      </Column>
    </Accessory>
  );
}

function TransactionSummary({ loan }: { loan: PostLoanContext }) {
  const { t } = useTranslation('posts');

  return (
    <Row alignItems="center" gap="spacing.general.x3" style={styles.contextCard}>
      <Column style={styles.contextThumbnailWrap}>
        <ListingThumbnail
          hideCorpoBadge
          picture={loan.listing.picture}
          size={64}
          subtype={loan.listing.subtype}
          type={loan.listing.type}
        />
      </Column>
      <Column flex={1} gap="spacing.general.x1" style={styles.contextBody}>
        <Text fontSize={11} style={styles.contextLabel}>
          {t('newPost.exchangeLabel')}
        </Text>
        <Text fontWeight="700" numberOfLines={1} style={styles.contextTitle} variant="bodyBase">
          {loan.listing.item.name}
        </Text>
        <Text color="text.secondary" fontSize={12}>
          {t('newPost.exchangeHint')}
        </Text>
      </Column>
    </Row>
  );
}

const styles = StyleSheet.create({
  actionBar: {
    backgroundColor: theme.colors['icon.inverse.primary'],
    borderTopColor: theme.colors['border.secondary'],
    borderTopWidth: 1,
    paddingHorizontal: theme.spacing['spacing.general.x6'],
    paddingTop: theme.spacing['spacing.general.x2'],
    boxShadow: '0px -6px 16px rgba(0, 0, 0, 0.05)',
  },
  captionCount: { color: theme.colors['icon.secondary'], textAlign: 'right' },
  captionField: {
    backgroundColor: theme.colors['surface.secondary-neutral'],
    borderColor: theme.colors['border.secondary'],
    borderRadius: theme.borderRadii['radius.lg'],
    borderWidth: 1,
    paddingHorizontal: theme.spacing['spacing.general.x3'],
    paddingVertical: theme.spacing['spacing.general.x3'],
  },
  captionHint: { color: theme.colors['icon.secondary'], lineHeight: 18 },
  captionInput: { fontSize: 16, lineHeight: 24, minHeight: 120, padding: 0 },
  contextBody: { minWidth: 0 },
  contextCard: {
    backgroundColor: theme.colors['icon.inverse.primary'],
    borderColor: theme.colors['border.secondary'],
    borderRadius: theme.borderRadii['radius.xl'],
    borderWidth: 1,
    padding: theme.spacing['spacing.general.x3'],
  },
  contextLabel: {
    color: theme.colors['text.brand'],
    fontWeight: '700',
    letterSpacing: 0.5,
    textTransform: 'uppercase',
  },
  contextThumbnailWrap: { borderRadius: theme.borderRadii['radius.xl'], overflow: 'hidden' },
  contextTitle: { fontSize: 16, lineHeight: 20 },
  noticeChip: {
    backgroundColor: theme.colors['surface.secondary-neutral'],
    borderColor: theme.colors['border.secondary'],
    borderRadius: theme.borderRadii['radius.xl'],
    borderWidth: 1,
    padding: theme.spacing['spacing.general.x3'],
  },
  noticeDismiss: { padding: theme.spacing['spacing.general.x1'] },
  noticeEyebrow: {
    color: theme.colors['icon.tertiary'],
    fontSize: 10,
    fontWeight: '700',
    letterSpacing: 0.6,
    textTransform: 'uppercase',
  },
  noticeIcon: { backgroundColor: theme.colors['surface.brand.subtle'], borderRadius: 999, height: 40, width: 40 },
  noticeText: { color: theme.colors['icon.secondary'], lineHeight: 18 },
  optionalLabel: { color: theme.colors['text.placeholder'] },
  pageContent: { paddingBottom: theme.spacing['spacing.general.x6'] },
  section: { paddingHorizontal: theme.spacing['spacing.general.x1'] },
});
